// =============================================================================
// Investor Announcements Page
// =============================================================================

import { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { ArchiveBoxIcon, ArchiveBoxXMarkIcon } from '@heroicons/react/24/outline';
import { Card, Button } from '../../components/ui';
import api from '../../services/api';
import { formatDateTime } from '../../utils/dateFormat';

interface Announcement {
  id: string;
  subject: string;
  body: string;
  dealName: string | null;
  sentAt: string;
  readAt: string | null;
  archivedAt: string | null;
}

type FilterType = 'all' | 'unread' | 'archived';

export function AnnouncementsPage() {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState<Announcement | null>(null);
  const [filter, setFilter] = useState<FilterType>('all');
  const [archiving, setArchiving] = useState<string | null>(null);

  useEffect(() => {
    loadAnnouncements();
  }, [filter]);

  const loadAnnouncements = async () => {
    try {
      setLoading(true);
      const response = await api.get<{ success: boolean; data: Announcement[] }>(
        `/investor/announcements?filter=${filter}`
      );
      setAnnouncements(response.data.data);
    } catch (err) {
      setError('Failed to load announcements');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAnnouncement = async (announcement: Announcement) => {
    setSelectedAnnouncement(announcement);

    // Mark as read if not already read
    if (!announcement.readAt) {
      try {
        // Instantly decrement sidebar badge + notification bell (no API wait)
        window.dispatchEvent(new CustomEvent('badge-decrement', { detail: { announcements: -1, announcementId: announcement.id } }));
        // Optimistically update local list
        setAnnouncements((prev) =>
          prev.map((a) => (a.id === announcement.id ? { ...a, readAt: new Date().toISOString() } : a))
        );
        // Then persist to server
        await api.put(`/investor/announcements/${announcement.id}/read`);
      } catch (err) {
        console.error('Failed to mark announcement as read:', err);
      }
    }
  };

  const handleArchive = async (announcementId: string) => {
    setArchiving(announcementId);
    try {
      await api.put(`/investor/announcements/${announcementId}/archive`);
      // Remove from list if not viewing archived
      if (filter !== 'archived') {
        setAnnouncements((prev) => prev.filter((a) => a.id !== announcementId));
        if (selectedAnnouncement?.id === announcementId) {
          setSelectedAnnouncement(null);
        }
      } else {
        setAnnouncements((prev) =>
          prev.map((a) => (a.id === announcementId ? { ...a, archivedAt: new Date().toISOString() } : a))
        );
      }
    } catch (err) {
      console.error('Failed to archive announcement:', err);
    } finally {
      setArchiving(null);
    }
  };

  const handleUnarchive = async (announcementId: string) => {
    setArchiving(announcementId);
    try {
      await api.put(`/investor/announcements/${announcementId}/unarchive`);
      // Remove from archived view
      if (filter === 'archived') {
        setAnnouncements((prev) => prev.filter((a) => a.id !== announcementId));
        if (selectedAnnouncement?.id === announcementId) {
          setSelectedAnnouncement(null);
        }
      } else {
        setAnnouncements((prev) =>
          prev.map((a) => (a.id === announcementId ? { ...a, archivedAt: null } : a))
        );
      }
    } catch (err) {
      console.error('Failed to unarchive announcement:', err);
    } finally {
      setArchiving(null);
    }
  };

  // Use centralized PST date formatting
  const formatDate = formatDateTime;

  const unreadCount = announcements.filter((a) => !a.readAt).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-900" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12 text-red-600">{error}</div>
    );
  }

  const filterTabs: { key: FilterType; label: string }[] = [
    { key: 'all', label: 'Inbox' },
    { key: 'unread', label: 'Unread' },
    { key: 'archived', label: 'Archived' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Announcements</h1>
          <p className="text-gray-500 mt-1">
            {unreadCount > 0 ? `${unreadCount} unread announcement${unreadCount > 1 ? 's' : ''}` : 'All announcements read'}
          </p>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 border-b border-gray-200 pb-2">
        {filterTabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => {
              setFilter(tab.key);
              setSelectedAnnouncement(null);
            }}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
              filter === tab.key
                ? 'bg-primary-100 text-primary-700 border-b-2 border-primary-600'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {announcements.length === 0 ? (
        <Card className="p-12 text-center">
          <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
          </svg>
          <p className="text-gray-500">No announcements yet</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Announcement List */}
          <div className="lg:col-span-1">
            <Card className="divide-y max-h-[600px] overflow-y-auto">
              {announcements.map((announcement) => (
                <div
                  key={announcement.id}
                  className={`p-4 hover:bg-gray-50 ${
                    selectedAnnouncement?.id === announcement.id ? 'bg-primary-50' : ''
                  } ${!announcement.readAt && !announcement.archivedAt ? 'border-l-4 border-l-primary-600' : ''}`}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className="flex-1 min-w-0 cursor-pointer"
                      onClick={() => handleSelectAnnouncement(announcement)}
                    >
                      <h3 className={`text-sm truncate ${!announcement.readAt ? 'font-semibold' : 'font-medium'} text-gray-900`}>
                        {announcement.subject}
                      </h3>
                      <p className="text-xs text-gray-500 mt-1">
                        {formatDate(announcement.sentAt)}
                      </p>
                      {announcement.dealName && (
                        <span className="inline-block mt-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">
                          {announcement.dealName}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {!announcement.readAt && !announcement.archivedAt && (
                        <span className="w-2 h-2 bg-primary-600 rounded-full" />
                      )}
                      {filter === 'archived' ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUnarchive(announcement.id);
                          }}
                          disabled={archiving === announcement.id}
                          className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                          title="Unarchive"
                        >
                          <ArchiveBoxXMarkIcon className="h-4 w-4" />
                        </button>
                      ) : (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleArchive(announcement.id);
                          }}
                          disabled={archiving === announcement.id}
                          className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                          title="Archive"
                        >
                          <ArchiveBoxIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </Card>
          </div>

          {/* Announcement Detail */}
          <div className="lg:col-span-2">
            {selectedAnnouncement ? (
              <Card className="p-6">
                <div className="mb-4">
                  <div className="flex items-start justify-between">
                    <h2 className="text-xl font-semibold text-gray-900">{selectedAnnouncement.subject}</h2>
                    {selectedAnnouncement.archivedAt ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleUnarchive(selectedAnnouncement.id)}
                        disabled={archiving === selectedAnnouncement.id}
                        className="flex items-center gap-1"
                      >
                        <ArchiveBoxXMarkIcon className="h-4 w-4" />
                        Unarchive
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleArchive(selectedAnnouncement.id)}
                        disabled={archiving === selectedAnnouncement.id}
                        className="flex items-center gap-1"
                      >
                        <ArchiveBoxIcon className="h-4 w-4" />
                        Archive
                      </Button>
                    )}
                  </div>
                  <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                    <span>{formatDate(selectedAnnouncement.sentAt)}</span>
                    {selectedAnnouncement.dealName && (
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
                        {selectedAnnouncement.dealName}
                      </span>
                    )}
                    {selectedAnnouncement.archivedAt && (
                      <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
                        Archived
                      </span>
                    )}
                  </div>
                </div>
                <div className="prose max-w-none border-t pt-4">
                  <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedAnnouncement.body) }} />
                </div>
              </Card>
            ) : (
              <Card className="p-12 text-center">
                <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
                <p className="text-gray-500">Select an announcement to read</p>
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AnnouncementsPage;
